/**
 * Loads the certified status for a course id from its Firestore doc.
 * Callers treat `error` as certified so a transient failure keeps the normal billing flow.
 */
import type { Course } from '@physiofit-digital/physiofit_types';
import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../../firebaseConfig';
import { isCourseCertified } from '../insurance/courseCertification';

const courseDocRef = (courseId: string) => doc(db, 'content', 'courses', 'courses', courseId);

export type CourseCertifiedResult =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; certified: boolean };

type ReadyPayload = { courseId: string; result: CourseCertifiedResult };

export function useCourseCertified(courseId: string): CourseCertifiedResult {
  const trimmedId = courseId.trim();
  const [payload, setPayload] = useState<ReadyPayload | null>(null);

  useEffect(() => {
    if (!trimmedId) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const snap = await getDoc(courseDocRef(trimmedId));
        if (cancelled) {
          return;
        }
        if (!snap.exists()) {
          setPayload({ courseId: trimmedId, result: { status: 'error' } });
          return;
        }
        const certified = isCourseCertified(snap.data() as Course);
        setPayload({ courseId: trimmedId, result: { status: 'ready', certified } });
      } catch {
        if (!cancelled) {
          setPayload({ courseId: trimmedId, result: { status: 'error' } });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [trimmedId]);

  if (!trimmedId) {
    return { status: 'error' };
  }
  if (!payload || payload.courseId !== trimmedId) {
    return { status: 'loading' };
  }
  return payload.result;
}
